import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { MediaTrack } from "@hybrid-iptv/player";
import { usePlayerStore } from "./playerStore.js";
import { useProfiles } from "../settings/profileStore.js";

/**
 * Sound and subtitles, remembered per live channel and per profile (owner, 2026-10-12).
 *
 * A track's id is opaque and changes between plays, so what is kept is the language and the
 * label (and the position as a last resort), never the id. Only a viewer's own choice is
 * recorded; putting a remembered choice back (player/useTrackMemory.ts) never records.
 * Films and episodes are not touched: they have their own handling.
 */

/** What we keep about a chosen track. */
export interface TrackPick {
  language?: string;
  label: string;
  index: number;
}

export interface ChannelTracks {
  audio?: TrackPick;
  text?: TrackPick | "off";
  at: number;
}

export const MAX_ENTRIES = 300;

export function memoryKey(profileId: string, channelId: string): string {
  return `${profileId}:${channelId}`;
}

/** Describe the chosen track of one kind, from the list it came from. */
export function describePick(
  tracks: readonly MediaTrack[],
  kind: "audio" | "text",
  id: string | null,
): TrackPick | "off" | undefined {
  if (kind === "text" && id === null) return "off";
  const same = tracks.filter((track) => track.kind === kind);
  const index = same.findIndex((track) => track.id === id);
  if (index < 0) return undefined;
  const track = same[index]!;
  return { ...(track.language ? { language: track.language } : {}), label: track.label, index };
}

/** Find the saved choice among today's tracks: language and label, then language, then position. */
export function findTrack(tracks: readonly MediaTrack[], kind: "audio" | "text", pick: TrackPick): MediaTrack | undefined {
  const same = tracks.filter((track) => track.kind === kind);
  return (
    same.find((track) => track.language === pick.language && track.label === pick.label) ??
    (pick.language ? same.find((track) => track.language === pick.language) : undefined) ??
    same[pick.index]
  );
}

interface TrackMemoryStore {
  byKey: Record<string, ChannelTracks>;
  remember: (key: string, kind: "audio" | "text", pick: TrackPick | "off") => void;
}

export const useTrackMemory = create<TrackMemoryStore>()(
  persist(
    (set) => ({
      byKey: {},
      remember: (key, kind, pick) =>
        set((state) => {
          const next = { ...state.byKey, [key]: { ...state.byKey[key], [kind]: pick, at: Date.now() } };
          const keys = Object.keys(next);
          if (keys.length > MAX_ENTRIES) {
            keys.sort((a, b) => next[a]!.at - next[b]!.at);
            for (const old of keys.slice(0, keys.length - MAX_ENTRIES)) delete next[old];
          }
          return { byKey: next };
        }),
    }),
    { name: "slique-track-memory", version: 1 },
  ),
);

/** The memory key of the live channel playing now, or null (films, episodes, nothing playing). */
export function currentChannelKey(): string | null {
  const { source, recentChannels } = usePlayerStore.getState();
  if (source?.type !== "live") return null;
  // The same lookup the remote's favourite action uses: the playing channel is in the recent list.
  const channel = recentChannels.find((c) => c.name === source.title);
  const profileId = useProfiles.getState().activeProfileId;
  return channel && profileId ? memoryKey(profileId, channel.id) : null;
}

let lastViewerChoiceAt = 0;

/** When the viewer last chose a track themselves; a restore in progress stands down after it. */
export function viewerChoiceAt(): number {
  return lastViewerChoiceAt;
}

/** Record a viewer's choice. Call ONLY from a viewer's action, never when a choice is restored. */
export function recordViewerChoice(tracks: readonly MediaTrack[], kind: "audio" | "text", id: string | null): void {
  lastViewerChoiceAt = Date.now();
  const key = currentChannelKey();
  if (!key) return;
  const pick = describePick(tracks, kind, id);
  if (pick) useTrackMemory.getState().remember(key, kind, pick);
}

// A removed profile takes its choices with it.
useProfiles.subscribe((state) => {
  const { byKey } = useTrackMemory.getState();
  const alive = new Set(state.profiles.map((profile) => `${profile.id}:`));
  const kept = Object.fromEntries(
    Object.entries(byKey).filter(([key]) => [...alive].some((prefix) => key.startsWith(prefix))),
  );
  if (Object.keys(kept).length !== Object.keys(byKey).length) useTrackMemory.setState({ byKey: kept });
});
